// @ts-nocheck
import { useState } from "react";
import {
    Sparkles,
    CalendarDays,
    Mail,
    DollarSign,
    ChevronRight,
    ShieldCheck,
    RefreshCw,
    ArrowUpRight,
    XCircle,
} from "lucide-react";
import { useCurrentSubscriptionPlan, useSubscribePlan, useCancelSubscription, useStripeCards } from "core";
import BrowsePlan from "./plan/browse-plan/BrowsePlan";
import ConfirmationDialog from "components/ui/confirmationDialog";
import { toast } from "components/ui/toast";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = {
    date: (ts) => {
        if (!ts) return "—";
        return new Date(ts * 1000).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    },
    price: (p) => (typeof p === "number" ? `$${p.toFixed(2)}` : "—"),
    emails: (n) => {
        const val = n ?? 0;
        return val === 0 ? "Unlimited" : val.toLocaleString();
    },
};

// ─── status badge ──────────────────────────────────────────────────────────────
const STATUS = {
    active: { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/30", label: "Active" },
    trial: { dot: "bg-blue-500", badge: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/30", label: "Trial" },
    paused: { dot: "bg-amber-500", badge: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/30", label: "Paused" },
    cancelled: { dot: "bg-red-500", badge: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/30", label: "Cancelled" },
    expired: { dot: "bg-gray-400", badge: "bg-gray-50 text-content-secondary ring-gray-200 dark:bg-gray-500/10 dark:text-gray-400 dark:ring-gray-500/30", label: "Expired" },
};

function StatusBadge({ status = "active" }) {
    const cfg = STATUS[status] ?? STATUS.active;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold ring-1 ${cfg.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
}

// ─── skeleton ──────────────────────────────────────────────────────────────────
function PlanSkeleton() {
    return (
        <div className="rounded-2xl border border-line dark:border-white/[0.07] overflow-hidden animate-pulse bg-surface dark:bg-dark-800 shadow-sm">
            {/* Hero zone */}
            <div className="px-6 py-5 bg-gray-50 dark:bg-white/[0.02] border-b border-line-subtle dark:border-white/[0.06]">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-dark-600 shrink-0" />
                        <div className="space-y-2">
                            <div className="h-3 w-16 rounded-full bg-gray-200 dark:bg-dark-600" />
                            <div className="h-6 w-24 rounded-lg bg-gray-200 dark:bg-dark-600" />
                            <div className="h-2.5 w-36 rounded-full bg-gray-200 dark:bg-dark-600" />
                        </div>
                    </div>
                    <div className="text-right space-y-2">
                        <div className="h-8 w-16 rounded-lg bg-gray-200 dark:bg-dark-600 ml-auto" />
                        <div className="h-2.5 w-14 rounded-full bg-gray-200 dark:bg-dark-600 ml-auto" />
                    </div>
                </div>
            </div>
            {/* Stats zone — 2x2 grid */}
            <div className="grid grid-cols-2">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className={`p-4 space-y-2 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""} border-line-subtle dark:border-white/[0.05]`}
                    >
                        <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-dark-700" />
                        <div className="h-2.5 w-20 rounded-full bg-gray-100 dark:bg-dark-700" />
                        <div className="h-4 w-28 rounded-full bg-gray-100 dark:bg-dark-700" />
                    </div>
                ))}
            </div>
            {/* Footer zone */}
            <div className="px-5 py-3.5 flex items-center justify-between bg-gray-50/50 dark:bg-white/[0.01] border-t border-line-subtle dark:border-white/[0.06]">
                <div className="h-2.5 w-32 rounded-full bg-gray-100 dark:bg-dark-700" />
                <div className="flex gap-2">
                    <div className="h-8 w-20 rounded-lg bg-gray-100 dark:bg-dark-700" />
                    <div className="h-8 w-28 rounded-lg bg-gray-100 dark:bg-dark-700" />
                </div>
            </div>
        </div>
    );
}

function getPlanName(plan) {
    if (!plan) return "Free";
    const planId = plan.subscriptionPlanId != null ? Number(plan.subscriptionPlanId) : null;
    if (planId === 1) return "Pro";
    if (planId === 2) return "Basic";
    if (planId === 3) return "Pay-as-you-Go";

    let code = (plan.planCode ?? plan.code ?? "").toLowerCase();
    if (code === "pro") return "Pro";
    if (code === "basic") return "Basic";
    if (code === "pay-as-you-go" || code === "payg") return "Pay-as-you-Go";

    const price = plan.price != null ? Number(plan.price) : null;
    if (price === 19.99) return "Pro";
    if (price === 4.99) return "Basic";
    if (price === 0.99 || price === 1) return "Pay-as-you-Go";

    return "Free";
}

// ─── active plan card — three-zone layout ─────────────────────────────────────
function ActivePlanCard({ plan, onCancel, onUpgrade }) {
    const emailsOffer = plan?.emails_offer ?? plan?.emailsOffer ?? 0;
    const planName = getPlanName(plan);

    const stats = [
        {
            icon: CalendarDays,
            label: "Start Date",
            value: fmt.date(plan.currentPeriodStart),
            iconClass: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-50 dark:bg-blue-500/10",
        },
        {
            icon: RefreshCw,
            label: "Renewal Date",
            value: fmt.date(plan.currentPeriodEnd),
            iconClass: "text-violet-600 dark:text-violet-400",
            bg: "bg-violet-50 dark:bg-violet-500/10",
        },
        {
            icon: Mail,
            label: "Email Offer",
            value: `${fmt.emails(emailsOffer)} emails`,
            iconClass: "text-emerald-600 dark:text-emerald-400",
            bg: "bg-emerald-50 dark:bg-emerald-500/10",
        },
        {
            icon: DollarSign,
            label: "Plan Price",
            value: fmt.price(plan.price),
            iconClass: "text-amber-600 dark:text-amber-400",
            bg: "bg-amber-50 dark:bg-amber-500/10",
        },
    ];

    return (
        <div className="rounded-2xl overflow-hidden border border-line dark:border-white/[0.07] bg-surface dark:bg-dark-800 shadow-sm hover:shadow-md transition-shadow duration-300">

            <div className="px-6 py-5 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-blue-900/[0.08] dark:via-indigo-900/[0.06] dark:to-dark-800 border-b border-blue-100/60 dark:border-white/[0.06]">
                <div className="flex items-center justify-between gap-4">

                    {/* Left — icon + plan name + subtitle */}
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-md shadow-blue-500/25 shrink-0">
                            <ShieldCheck style={{ width: 20, height: 20 }} className="text-white" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h2 className="text-title-lg font-extrabold text-content dark:text-white tracking-tight leading-none">
                                    {planName}
                                </h2>
                                <StatusBadge status={plan.status} />
                            </div>
                            <p className="text-2xs font-bold text-content-muted dark:text-gray-400 leading-none">
                                Current Subscription &nbsp;·&nbsp; Billed Monthly
                            </p>
                        </div>
                    </div>

                    {/* Right — price block */}
                    <div className="text-right shrink-0">
                        <p className="text-[16px] font-black text-content dark:text-white tracking-tight leading-none">
                            {fmt.price(plan.price)}
                        </p>
                        <p className="text-[10px] font-bold text-content-subtle dark:text-gray-500 mt-1">per month</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2">
                {stats.map(({ icon: Icon, label, value, iconClass, bg }, idx) => (
                    <div
                        key={label}
                        className={[
                            "group p-4 flex flex-col gap-2 transition-colors hover:bg-gray-50/70 dark:hover:bg-white/[0.025]",
                            idx % 2 === 0 ? "border-r border-line-subtle dark:border-white/[0.05]" : "",
                            idx < 2 ? "border-b border-line-subtle dark:border-white/[0.05]" : "",
                        ].join(" ")}
                    >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${bg} transition-transform group-hover:scale-110 duration-200`}>
                            <Icon style={{ width: 14, height: 14 }} className={iconClass} />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-content-subtle dark:text-gray-500 uppercase tracking-widest mb-0.5">
                                {label}
                            </p>
                            <p className="text-ui-lg font-bold text-content dark:text-white leading-tight">
                                {value}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between gap-4 px-5 py-3.5 bg-gray-50/50 dark:bg-white/[0.01] border-t border-line-subtle dark:border-white/[0.06]">
                {/* Left — renewal info */}
                <p className="text-[11px] text-content-subtle dark:text-gray-500 font-medium hidden sm:block">
                    Renews on&nbsp;
                    <span className="font-semibold text-content-secondary dark:text-gray-300">
                        {fmt.date(plan.currentPeriodEnd)}
                    </span>
                </p>

                {/* Right — actions */}
                <div className="flex items-center gap-2 ml-auto">
                    <button
                        onClick={() => onCancel && onCancel(plan)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-semibold
                            text-content-muted dark:text-gray-400
                            border border-line dark:border-white/[0.09]
                            hover:text-red-600 dark:hover:text-red-400
                            hover:bg-red-50 dark:hover:bg-red-500/10
                            hover:border-red-200 dark:hover:border-red-500/20
                            transition-all duration-150"
                    >
                        <XCircle style={{ width: 13, height: 13 }} />
                        Cancel
                    </button>
                    <button
                        onClick={() => onUpgrade && onUpgrade(plan)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold
                            text-white
                            bg-gradient-to-r from-blue-600 to-violet-600
                            hover:from-blue-700 hover:to-violet-700
                            shadow-sm shadow-blue-500/25
                            transition-all duration-150 hover:shadow-blue-500/40 hover:shadow-md"
                    >
                        <Sparkles style={{ width: 13, height: 13 }} />
                        Update Plan
                        <ArrowUpRight style={{ width: 12, height: 12 }} className="opacity-70" />
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── no plan state ─────────────────────────────────────────────────────────────
function NoPlanState({ onBrowse, message }) {
    return (
        <div className="rounded-2xl border border-dashed border-line dark:border-white/[0.1] p-12 flex flex-col items-center text-center bg-gray-50/40 dark:bg-white/[0.01]">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center mb-5 shadow-lg shadow-blue-500/20">
                <Sparkles style={{ width: 24, height: 24 }} className="text-white" />
            </div>
            <h3 className="text-[15px] font-bold text-gray-800 dark:text-gray-100 mb-2">No Active Subscription</h3>
            <p className="text-ui text-content-muted dark:text-gray-400 max-w-xs mb-7 leading-relaxed">
                {message || "You don't have an active plan. Subscribe to unlock all features."}
            </p>
            <button
                onClick={onBrowse}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-[13px] font-bold px-5 py-2.5 rounded-xl shadow-sm shadow-blue-500/20 transition-all"
            >
                <Sparkles style={{ width: 14, height: 14 }} />
                Browse Plans
                <ChevronRight style={{ width: 14, height: 14 }} className="opacity-70" />
            </button>
        </div>
    );
}

// ─── main component ────────────────────────────────────────────────────────────
export default function SubscriptionsSection() {
    const [openBrowsePlan, setOpenBrowsePlan] = useState(false);
    const [openConfirmCancelPlan, setOpenConfirmCancelPlan] = useState(false);

    // ── API hooks ──────────────────────────────────────────────────────────────
    const {
        data: userCurrentPlan,
        isFetching: userPlanLoading,
    } = useCurrentSubscriptionPlan();

    const { data: userCards } = useStripeCards();

    const { mutateAsync: subscribePlanMutation, isPending: subscribing } = useSubscribePlan();
    const { mutateAsync: cancelPlan, isPending: cancelling } = useCancelSubscription();

    // ── handlers ───────────────────────────────────────────────────────────────
    const handleSubscribePlan = async (plan) => {
        if (!userCards?.length) {
            toast.error("Please add at least one payment method before subscribing.");
            return;
        }
        try {
            await subscribePlanMutation({ subscriptionPlanCode: plan.planCode });
            setOpenBrowsePlan(false);
        } catch (err) {
            toast.error(err?.response?.data?.message ?? "Failed to subscribe. Please try again.");
        }
    };

    const handleCancelPlan = async () => {
        try {
            await cancelPlan({ stripeSubscriptionId: userCurrentPlan?.stripeSubscriptionId });
            toast.success("Subscription cancelled.");
            setOpenConfirmCancelPlan(false);
        } catch (err) {
            toast.error(err?.response?.data?.message ?? "Failed to cancel. Please try again.");
        }
    };

    // ── render ─────────────────────────────────────────────────────────────────
    return (
        <section className="space-y-4">
            {/* Section header — no redundant CTA here */}
            <div>
                <h2 className="text-[15px] font-bold text-content dark:text-white">Subscription</h2>
                <p className="text-[13px] text-content-muted dark:text-gray-400 mt-0.5">Manage your active plan and billing</p>
            </div>

            {/* Plan content */}
            {userPlanLoading ? (
                <PlanSkeleton />
            ) : userCurrentPlan ? (
                <ActivePlanCard
                    plan={userCurrentPlan}
                    onCancel={() => setOpenConfirmCancelPlan(true)}
                    onUpgrade={() => setOpenBrowsePlan(true)}
                />
            ) : (
                <NoPlanState onBrowse={() => setOpenBrowsePlan(true)} />
            )}

            {/* Browse Plans modal */}
            <BrowsePlan
                open={openBrowsePlan}
                onClose={() => setOpenBrowsePlan(false)}
                onSubscribe={handleSubscribePlan}
                isSubscribing={subscribing}
                currentPlanInfo={userCurrentPlan}
            />

            {/* Cancel confirmation */}
            <ConfirmationDialog
                open={openConfirmCancelPlan}
                title="Cancel Subscription"
                description="Are you sure you want to cancel your current plan? You will lose access at the end of the billing period."
                confirmLabel={cancelling ? "Cancelling…" : "Yes, Cancel"}
                variant="destructive"
                onConfirm={handleCancelPlan}
                onCancel={() => setOpenConfirmCancelPlan(false)}
            />
        </section>
    );
}
